import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from 'react';
import { Box, Button, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, CircularProgress, Alert, Snackbar } from '@mui/material';
import Navbar from '../Layouts/Navbar';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddModal from "../PickUp/AddModal";
import api from "../../utils/api";

export default function DetailTrajets() {
  const [openadd, setOpenAdd] = useState(false);
  const [trajet, setTrajet] = useState(null);
  const [site, setSite] = useState(null);
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', type: 'success' });
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  
  const handleSnack = (message, type) => {
    setSnack({ open: true, message, type });
    setTimeout(() => {
      setSnack({ ...snack, open: false });
    }, 3000);
  };
  
  const handleCloseSnack = () => {
    setSnack({ ...snack, open: false });
  };
  
  // Handle duplicate trajet
  const handleDuplicate = async () => {
    try {
      if (trajet) {
        // Create a new trajet object with the same properties but a modified name
        const newTrajet = {
          ...trajet,
          name: `${trajet.name} - Copy`,
          // Remove the ID so the API will create a new record
          ID_Trajet: undefined
        };
        
        // Send the new trajet to the API
        await api.post('/trajets', newTrajet);
        handleSnack('Trajet dupliqué avec succès', 'success');
        
        // Navigate to trajets list page after successful duplication
        setTimeout(() => {
          navigate('/trajets');
        }, 1500); // Short delay to show the success message before navigating
      }
    } catch (error) {
      console.error('Error duplicating trajet:', error);
      handleSnack("Erreur lors de la duplication du trajet", 'error');
    }
  };

  // Get trajet ID from URL query parameters
  const getTrajetId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('id');
  };
  
  // Fetch pickup points for a specific trajet
  const fetchPickups = async (trajetId) => {
    try {
      const pickupsResponse = await api.get('/pickups');
      const filteredPickups = pickupsResponse.data.filter(pickup => 
        pickup.ID_Trajet === parseInt(trajetId) || pickup.ID_Trajet === trajetId
      );
      setPickups(filteredPickups);
    } catch (err) {
      console.error('Error fetching pickup points:', err);
      handleSnack('Erreur lors du chargement des points de ramassage', 'error');
    }
  };
  
  // Extract trajet ID from URL query parameters and fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const trajetId = getTrajetId();
        
        if (!trajetId) {
          setError('ID du trajet non spécifié');
          setLoading(false);
          return;
        }
        
        // Fetch trajet data
        const trajetResponse = await api.get(`/trajets/${trajetId}`);
        const trajetData = trajetResponse.data;
        setTrajet(trajetData);
        
        // Fetch site data if trajet has a site ID
        if (trajetData.ID_Site) {
          const siteResponse = await api.get(`/sites/${trajetData.ID_Site}`);
          const siteData = siteResponse.data;
          setSite(siteData);
          
          // Fetch societe data if site has a societe ID
          if (siteData.ID_Societe) {
            try {
              const societeResponse = await api.get(`/societes/${siteData.ID_Societe}`);
              // Update site data with societe information
              setSite(prevSite => ({
                ...prevSite,
                societe: societeResponse.data
              }));
            } catch (societeErr) {
              console.error('Error fetching societe details:', societeErr);
              // Continue without societe data
            }
          }
        }
        
        // Fetch pickup points for this trajet
        await fetchPickups(trajetId);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching trajet details:', err);
        setError(err.response?.data?.message || 'Erreur lors du chargement des données du trajet');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [location]);

  return (
    <>
      <Navbar />
      <Box p={3}>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress color="error" />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : trajet ? (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center">
                <AltRouteIcon />
                <Typography variant="h6" ml={1}>TRAJETS</Typography>
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  component={Link}
                  to="/trajets"
                  variant="outlined"
                  sx={{
                    color: 'red',
                    borderColor: 'red',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
                  }}
                  startIcon={<MenuIcon />}
                >
                  Liste
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDuplicate}
                  sx={{
                    color: 'red',
                    borderColor: 'red',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
                  }}
                  startIcon={<ContentCopyIcon />}
                >
                  Dupliquer
                </Button>
                <Button
                  onClick={handleOpenAdd}
                  variant="outlined"
                  sx={{
                    color: 'red',
                    borderColor: 'red',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
                  }}
                  startIcon={<AddIcon />}
                >
                  Ajouter un point de ramassage
                </Button>
              </Box>
            </Box>

            {/* Information Section */}
            <Box sx={{ backgroundColor: '#000', p: 1, mb: 2 }}>
              <Typography variant="subtitle1" color="white">
                INFORMATIONS DU TRAJET
              </Typography>
            </Box>

            {/* Main Data Section - Updated with spans */}
            <Box flex={1} pr={2}>
              <Box display="flex" py={1}>
                <Box width="25%">
                  <Typography sx={{ fontWeight: 'bold' }}>Nom</Typography>
                </Box>
                <Box width="25%">
                  <Typography>{trajet.name || 'N/A'}</Typography>
                </Box>
                <Box width="25%">
                  <Typography sx={{ fontWeight: 'bold' }}>Statut</Typography>
                </Box>
                <Box width="25%">
                  <Box
                    sx={{
                      backgroundColor: trajet.Status === 'active' || trajet.Status === 'Active' ? '#4caf50' : '#dc3545',
                      color: 'white',
                      borderRadius: '4px',
                      px: 1,
                      py: 0.5,
                      display: 'inline-block',
                      fontSize: '0.8rem',
                      textAlign: 'center',
                      minWidth: '60px',
                    }}
                  >
                    {trajet.Status === 'active' || trajet.Status === 'Active' ? 'Actif' : 'Inactif'}
                  </Box>
                </Box>
              </Box>
              <Divider />

              <Box display="flex" py={1}>
                <Box width="25%">
                  <Typography sx={{ fontWeight: 'bold' }}>Société</Typography>
                </Box>
                <Box width="25%">
                  <Typography>{site?.societe?.name || (site && site.ID_Societe ? `ID: ${site.ID_Societe}` : 'N/A')}</Typography>
                </Box>
                <Box width="25%">
                  <Typography sx={{ fontWeight: 'bold' }}>Site</Typography>
                </Box>
                <Box width="25%">
                  <Typography>{site?.name || (trajet.ID_Site ? `ID: ${trajet.ID_Site}` : 'N/A')}</Typography>
                </Box>
              </Box>
              <Divider />
            </Box>

            {/* Pickup Points Section */}
            <Box sx={{ backgroundColor: '#000', p: 1, mb: 2, mt: 2 }}>
              <Typography variant="subtitle1" color="white">
                POINTS DE RAMASSAGE
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Latitude</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Longitude</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pickups.length > 0 ? (
                    pickups.map((pickup) => (
                      <TableRow key={pickup.id_pickup} hover>
                        <TableCell>{pickup.name}</TableCell>
                        <TableCell>{pickup.latitude ? `${pickup.latitude}.0000000`.split('.').slice(0, 2).join('.') : ''}</TableCell>
                        <TableCell>{pickup.longitude ? `${pickup.longitude}.0000000`.split('.').slice(0, 2).join('.') : ''}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              backgroundColor: pickup.Status === 'active' || pickup.Status === 'Active' ? '#4caf50' : '#dc3545',
                              color: 'white',
                              borderRadius: '4px',
                              px: 1,
                              py: 0.5,
                              display: 'inline-block',
                              fontSize: '0.8rem',
                              textAlign: 'center',
                              minWidth: '60px',
                            }}
                          >
                            {pickup.Status === 'active' || pickup.Status === 'Active' ? 'Actif' : 'Inactif'}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">Aucun point de ramassage trouvé</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <AddModal 
              open={openadd} 
              handleClose={handleCloseAdd} 
              handleSnack={handleSnack} 
              onSuccess={() => fetchPickups(getTrajetId())} 
              trajets={[trajet]} 
            />
            
            {/* Snackbar for notifications */}
            <Snackbar
              open={snack.open}
              autoHideDuration={3000}
              onClose={handleCloseSnack}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnack} severity={snack.type} sx={{ width: '100%' }}>
                {snack.message}
              </Alert>
            </Snackbar>
          </>
        ) : (
          <Alert severity="info">Aucune donnée de trajet trouvée</Alert>
        )}
      </Box>
    </>
  );
}
